import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import FormModal from 'components/shared/modal/FormModal';
import Button, {
  BUTTON_TYPES,
  BUTTON_COLORS,
} from 'components/shared/button/Button';
import SignaturePadModal from 'components/admin/projects/details/project-rfa-log/SignaturePadModal';
import UserSignaturesApi from 'services/api/user-signatures-api';
import { showErrorMessage } from 'actions/messages';
import { getDarkMode } from 'selectors/theme';
import { dataUrlToFile } from 'services/utils/files-util';
import './ManageSignaturesModal.css';

// Component to handle image loading with fallbacks
const SignatureImage = ({ signature }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Reset state when signature changes
    setHasError(false);
    setRetryCount(0);

    // Prefer data URL, fallback to regular URL
    let url =
      signature.signature_image_data_url &&
      signature.signature_image_data_url !== 'null' &&
      signature.signature_image_data_url !== null
        ? signature.signature_image_data_url
        : signature.signature_image_url || null;

    // Add timestamp to S3 URLs to avoid cache issues
    if (
      url &&
      url.includes('s3-accelerate.amazonaws.com') &&
      !url.includes('?')
    ) {
      url = `${url}?t=${new Date().getTime()}`;
    }

    setImageUrl(url);
  }, [signature]);

  const handleError = () => {
    if (
      retryCount < 2 &&
      imageUrl &&
      imageUrl.includes('s3-accelerate.amazonaws.com')
    ) {
      // Retry with fresh timestamp
      const baseUrl = imageUrl.split('?')[0];
      setImageUrl(`${baseUrl}?t=${new Date().getTime()}`);
      setRetryCount((prev) => prev + 1);
    } else {
      setHasError(true);
    }
  };

  if (!imageUrl || hasError) {
    return <div className="no-image">No image available</div>;
  }

  return (
    <img
      src={imageUrl}
      alt={signature.label || 'Signature'}
      onError={handleError}
      onLoad={() => setHasError(false)}
    />
  );
};

const ManageSignaturesModal = ({ show, onClose, dispatch, darkMode }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [pendingSignatureData, setPendingSignatureData] = useState(null);
  const [labelInput, setLabelInput] = useState('');
  const [editingSignature, setEditingSignature] = useState(null);

  useEffect(() => {
    if (show) {
      loadSignatures();
    } else {
      setSignatures([]);
      setEditingSignature(null);
      setLabelInput('');
      setPendingSignatureData(null);
      setShowLabelInput(false);
    }
  }, [show]);

  const loadSignatures = async () => {
    setLoading(true);
    try {
      const response = await UserSignaturesApi.getUserSignatures();
      setLoading(false);

      if (response.ok) {
        // Process signatures to ensure image URLs are valid
        const processedSignatures = (response.data || []).map((sig) => ({
          ...sig,
          // Ensure we have a valid image URL
          signature_image_url: sig.signature_image_url || null,
          signature_image_data_url: sig.signature_image_data_url || null,
        }));
        setSignatures(processedSignatures);
      } else {
        // If it's a 500 error, it's likely a backend issue with signature_image_data_url
        // Still show the error but don't crash the modal
        const errorMessage = response.errors || ['Failed to load signatures'];
        if (
          errorMessage.some &&
          errorMessage.some(
            (err) =>
              err.includes('Internal Server Error') || err.includes('body')
          )
        ) {
          dispatch(
            showErrorMessage([
              'There was an issue loading signatures. Some signatures may not display correctly.',
            ])
          );
          // Try to continue with empty array or partial data
          setSignatures([]);
        } else {
          dispatch(showErrorMessage(errorMessage));
        }
      }
    } catch (error) {
      setLoading(false);
      dispatch(
        showErrorMessage(['Failed to load signatures. Please try again later.'])
      );
      setSignatures([]);
    }
  };

  const handleCreateSignature = () => {
    setEditingSignature(null);
    setLabelInput('');
    setShowSignaturePad(true);
  };

  const handleEditSignature = (signature) => {
    setEditingSignature(signature);
    setLabelInput(signature.label || '');
    setShowSignaturePad(true);
  };

  const handleSignaturePadSave = async (dataUrl) => {
    // Store the data URL and show label input
    // IMPORTANT: Don't reset editingSignature here - we need it for the label modal
    setPendingSignatureData(dataUrl);
    setShowSignaturePad(false);
    setShowLabelInput(true);
  };

  const handleSignaturePadCancel = () => {
    // Only reset editingSignature when user explicitly cancels
    setShowSignaturePad(false);
    setEditingSignature(null);
    setLabelInput('');
  };

  const handleSaveWithLabel = async () => {
    if (!pendingSignatureData) return;

    try {
      // Use the utility function to convert data URL to File
      const file = await dataUrlToFile(pendingSignatureData, 'signature.png');

      setLoading(true);
      let response;

      if (editingSignature) {
        const updates = {
          signatureImage: file,
        };
        if (labelInput.trim()) {
          updates.label = labelInput.trim();
        }
        response = await UserSignaturesApi.updateUserSignature(
          editingSignature.id,
          updates
        );
      } else {
        response = await UserSignaturesApi.createUserSignature(
          file,
          labelInput.trim() || null,
          false
        );
      }

      setLoading(false);

      if (response.ok) {
        // Small delay to ensure backend has processed the file
        setTimeout(async () => {
          await loadSignatures();
        }, 500);
        setShowLabelInput(false);
        setPendingSignatureData(null);
        setEditingSignature(null);
        setLabelInput('');
      } else {
        dispatch(
          showErrorMessage(response.errors || ['Failed to save signature'])
        );
      }
    } catch (error) {
      setLoading(false);
      dispatch(showErrorMessage(['Failed to process signature image']));
    }
  };

  const handleDeleteSignature = async (signatureId) => {
    if (!window.confirm('Are you sure you want to delete this signature?')) {
      return;
    }

    setLoading(true);
    const response = await UserSignaturesApi.deleteUserSignature(signatureId);
    setLoading(false);

    if (response.ok) {
      await loadSignatures();
    } else {
      dispatch(
        showErrorMessage(response.errors || ['Failed to delete signature'])
      );
    }
  };

  const handleSetDefault = async (signatureId) => {
    setLoading(true);

    try {
      // First, find and unset the current default signature if it exists
      const currentDefault = signatures.find(
        (sig) => sig.is_default && sig.id !== signatureId
      );

      if (currentDefault) {
        // Unset the current default first
        const unsetResponse = await UserSignaturesApi.updateUserSignature(
          currentDefault.id,
          { isDefault: false }
        );

        if (!unsetResponse.ok) {
          setLoading(false);
          dispatch(
            showErrorMessage(
              unsetResponse.errors || [
                'Failed to unset current default signature',
              ]
            )
          );
          return;
        }
      }

      // Now set the new signature as default
      const response = await UserSignaturesApi.setDefaultSignature(signatureId);
      setLoading(false);

      if (response.ok) {
        await loadSignatures();
      } else {
        dispatch(
          showErrorMessage(
            response.errors || ['Failed to set default signature']
          )
        );
      }
    } catch (error) {
      setLoading(false);
      dispatch(showErrorMessage(['Failed to set default signature']));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <FormModal
        show={show && !showSignaturePad && !showLabelInput}
        onClose={onClose}
        title="Manage Signatures"
        className="manage-signatures-modal"
        actions={[
          <Button
            key="add"
            type={BUTTON_TYPES.PRIMARY}
            color={BUTTON_COLORS.GREEN}
            label="Add Signature"
            onClick={handleCreateSignature}
          />,
        ]}
      >
        <div className="manage-signatures-content">
          {loading && signatures.length === 0 ? (
            <div className="loading-message">Loading signatures...</div>
          ) : signatures.length === 0 ? (
            <div className="empty-state">
              <p>You don't have any signatures yet.</p>
              <p>Click "Add Signature" to create your first signature.</p>
            </div>
          ) : (
            <div className="signatures-list">
              {signatures.map((signature) => (
                <div
                  key={signature.id}
                  className={`signature-item ${
                    signature.is_default ? 'default' : ''
                  }`}
                >
                  <div className="signature-preview">
                    <SignatureImage signature={signature} />
                  </div>
                  <div className="signature-info">
                    <div className="signature-label">
                      {signature.label || 'Untitled Signature'}
                      {signature.is_default && (
                        <span className="default-badge">Default</span>
                      )}
                    </div>
                    <div className="signature-date">
                      Created: {formatDate(signature.created_at)}
                    </div>
                  </div>
                  <div className="signature-actions">
                    {!signature.is_default && (
                      <Button
                        type={BUTTON_TYPES.SECONDARY}
                        color={BUTTON_COLORS.GRAY}
                        label="Set as Default"
                        onClick={() => handleSetDefault(signature.id)}
                      />
                    )}
                    <Button
                      type={BUTTON_TYPES.SECONDARY}
                      color={BUTTON_COLORS.GRAY}
                      label="Edit"
                      onClick={() => handleEditSignature(signature)}
                    />
                    <Button
                      type={BUTTON_TYPES.SECONDARY}
                      color={BUTTON_COLORS.GRAY}
                      label="Delete"
                      onClick={() => handleDeleteSignature(signature.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </FormModal>

      <SignaturePadModal
        show={showSignaturePad}
        onClose={handleSignaturePadCancel}
        onSave={handleSignaturePadSave}
        mode="draw"
        initialImageUrl={
          editingSignature
            ? editingSignature.signature_image_data_url &&
              editingSignature.signature_image_data_url !== 'null' &&
              editingSignature.signature_image_data_url !== null
              ? editingSignature.signature_image_data_url
              : editingSignature.signature_image_url
            : null
        }
      />

      <FormModal
        show={showLabelInput}
        onClose={() => {
          setShowLabelInput(false);
          setPendingSignatureData(null);
          setEditingSignature(null);
          setLabelInput('');
        }}
        title={
          editingSignature ? 'Edit Signature Label' : 'Add Signature Label'
        }
        className="signature-label-input-modal"
        actions={[
          <Button
            key="cancel"
            type={BUTTON_TYPES.SECONDARY}
            color={BUTTON_COLORS.GRAY}
            label="Cancel"
            onClick={() => {
              setShowLabelInput(false);
              setPendingSignatureData(null);
              setEditingSignature(null);
              setLabelInput('');
            }}
          />,
          <Button
            key="save"
            type={BUTTON_TYPES.PRIMARY}
            color={BUTTON_COLORS.GREEN}
            label="Save Signature"
            onClick={handleSaveWithLabel}
            disabled={loading}
          />,
        ]}
      >
        <div className="label-input-container">
          <label htmlFor="signature-label">Label (optional):</label>
          <input
            id="signature-label"
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder="e.g., Personal Signature, Work Signature"
            maxLength={255}
            className="label-input"
          />
          <p className="label-hint">
            Give your signature a name to easily identify it later.
          </p>
        </div>
      </FormModal>
    </>
  );
};

export default connect((state) => ({
  darkMode: getDarkMode(state),
}))(ManageSignaturesModal);
